import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useStaticQuery, graphql, Link } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import sr from '@utils/sr';
import { srConfig } from '@config';
import { Icon } from '@components/icons';
import Modal from '@components/modal';
import { usePrefersReducedMotion } from '@hooks';
import { sortEdgesByOrder } from '@utils/sort';
import { normalizeUrl } from '@utils/url';

const StyledProjectsGrid = styled.ul`
  ${({ theme }) => theme.mixins.resetList};
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin-top: 32px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }

  a {
    position: relative;
    z-index: 1;
  }
`;

const StyledViewAll = styled.div`
  margin-top: 28px;
  display: flex;
  justify-content: center;

  a {
    ${({ theme }) => theme.mixins.bigButton};
  }
`;

const StyledFilters = styled.ul`
  ${({ theme }) => theme.mixins.resetList};
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 25px;
  margin-bottom: 25px;
  justify-content: center;
`;

const StyledFilterButton = styled.button`
  ${({ theme }) => theme.mixins.smallButton};
  padding: 10px 14px;
  background-color: transparent;
  color: ${({ isActive }) => (isActive ? 'var(--green)' : 'var(--light-slate)')};
  border-color: ${({ isActive }) => (isActive ? 'var(--green)' : 'var(--light-slate)')};

  &:hover,
  &:focus {
    background-color: var(--lightest-navy);
    color: var(--green);
    border-color: var(--green);
  }
`;

const StyledProject = styled.li`
  position: relative;
  ${({ theme }) => theme.mixins.boxShadow};
  background-color: var(--light-navy);
  border-radius: var(--border-radius);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;

  .project-content {
    position: relative;
    padding: 1.25rem 1.5rem 1.35rem;
    display: flex;
    flex-direction: column;
    justify-content: center;

    @media (max-width: 480px) {
      padding: 1.1rem 1.2rem 1.25rem;
    }
  }

  .project-overline {
    margin: 10px 0;
    color: var(--green);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    font-weight: 400;
  }

  .project-title {
    color: var(--lightest-slate);
    font-size: clamp(24px, 5vw, 28px);
    margin: 0 0 14px;
  }

  .project-description {
    position: relative;
    z-index: 2;
    color: var(--light-slate);
    font-size: var(--fz-lg);
    line-height: 1.6;
    margin: 0;

    a {
      ${({ theme }) => theme.mixins.inlineLink};
    }

    strong {
      color: var(--white);
      font-weight: normal;
    }
  }

  .readmore-link {
    ${({ theme }) => theme.mixins.inlineLink};
    background: transparent;
    border: 0;
    padding: 0;
    cursor: pointer;
    font: inherit;
  }

  .project-category {
    margin-top: 12px;
    color: var(--slate);
    font-family: var(--font-mono);
    font-size: var(--fz-xxs);
    display: inline-flex;
    gap: 8px;
    align-items: baseline;
    padding: 6px 10px;
    border-radius: 999px;
    border: 1px solid var(--lightest-navy);
    background-color: rgba(2, 12, 27, 0.35);
    width: fit-content;

    .label {
      color: var(--light-slate);
    }
  }

  .project-tech-list {
    display: flex;
    flex-wrap: wrap;
    position: relative;
    z-index: 2;
    margin: 14px 0 8px;
    padding: 0;
    list-style: none;

    li {
      margin: 0 20px 5px 0;
      color: var(--light-slate);
      font-family: var(--font-mono);
      font-size: var(--fz-xs);
      white-space: nowrap;
    }

    li {
      margin: 0 14px 6px 0;
      color: var(--light-slate);
      font-family: var(--font-mono);
      font-size: var(--fz-xs);
      white-space: nowrap;
    }
  }

  .project-links {
    display: flex;
    align-items: center;
    position: relative;
    margin-top: 10px;
    color: var(--lightest-slate);
    gap: 10px;
    flex-wrap: wrap;

    a,
    span,
    button {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      font-family: var(--font-mono);
      font-size: var(--fz-xxs);
      padding: 8px 12px;
      border-radius: 999px;
      border: 1px solid var(--lightest-navy);
      background-color: rgba(2, 12, 27, 0.25);
      white-space: nowrap;
    }

    a,
    button {
      color: var(--green);
      cursor: pointer;

      &:hover,
      &:focus {
        background-color: rgba(100, 255, 218, 0.08);
        border-color: var(--green);
      }
    }

    .secondary {
      color: var(--light-slate);
    }

    .status {
      color: var(--slate);
    }

    svg {
      width: 16px;
      height: 16px;
    }
  }

  .project-image {
    position: relative;
    width: 100%;
    min-height: 190px;
    background-color: var(--navy);
    display: flex;
    align-items: center;
    justify-content: center;
    border-bottom: 1px solid rgba(136, 146, 176, 0.15);

    @media (max-width: 480px) {
      min-height: 170px;
    }

    a {
      display: flex;
      width: 100%;
      height: 100%;
      align-items: center;
      justify-content: center;
      padding: 12px;
    }

    .img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      border-radius: 0;
    }

    .img img {
      object-fit: contain !important;
    }
  }
`;

const Featured = ({ limit = 6, showViewAllLink = true, showHeading = true }) => {
  const data = useStaticQuery(graphql`
    {
      featured: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/user/featured/" } }
        sort: { fields: [frontmatter___date], order: ASC }
      ) {
        edges {
          node {
            frontmatter {
              title
              date
              order
              description
              cover {
                childImageSharp {
                  gatsbyImageData(width: 700, placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
                }
              }
              tech
              github
              external
              cta
              category
            }
            html
          }
        }
      }
    }
  `);

  const featuredProjects = sortEdgesByOrder(
    data.featured.edges.filter(({ node }) => node),
    (a, b) => {
      const aDate = a?.node?.frontmatter?.date ? new Date(a.node.frontmatter.date).getTime() : null;
      const bDate = b?.node?.frontmatter?.date ? new Date(b.node.frontmatter.date).getTime() : null;
      const aMs = Number.isFinite(aDate) ? aDate : Infinity;
      const bMs = Number.isFinite(bDate) ? bDate : Infinity;
      if (aMs !== bMs) {
        return aMs - bMs;
      }
      return String(a?.node?.frontmatter?.title || '').localeCompare(
        String(b?.node?.frontmatter?.title || ''),
      );
    },
  );
  if (!featuredProjects || featuredProjects.length === 0) {
    return null;
  }
  const revealTitle = useRef(null);
  const revealProjects = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    if (revealTitle.current) {
      sr.reveal(revealTitle.current, srConfig());
    }
    revealProjects.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 100)));
  }, []);

  const categoryOptions = useMemo(() => {
    const excluded = new Set(['business website']);
    const categories = new Set();
    featuredProjects.forEach(({ node }) => {
      const raw = node.frontmatter?.category;
      if (!raw) {
        return;
      }
      String(raw)
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
        .forEach(c => {
          if (excluded.has(String(c).toLowerCase())) {
            return;
          }
          categories.add(c);
        });
    });
    return ['All', ...Array.from(categories)];
  }, [featuredProjects]);

  const [activeCategory, setActiveCategory] = useState('All');
  const [modal, setModal] = useState({ isOpen: false, title: '', html: '' });
  const filteredFeatured = useMemo(() => {
    if (activeCategory === 'All') {
      return featuredProjects;
    }
    return featuredProjects.filter(({ node }) => {
      const raw = node.frontmatter?.category;
      if (!raw) {
        return false;
      }
      return String(raw)
        .split(',')
        .map(s => s.trim())
        .includes(activeCategory);
    });
  }, [activeCategory, featuredProjects]);

  useEffect(() => {
    if (!categoryOptions.includes(activeCategory)) {
      setActiveCategory('All');
    }
  }, [activeCategory, categoryOptions]);

  const openModal = ({ title, html }) => setModal({ isOpen: true, title, html });
  const closeModal = () => setModal(m => ({ ...m, isOpen: false }));

  const plainTextFromHtml = value =>
    String(value || '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const escapeHtml = value =>
    String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  const truncateText = (text, maxChars = 260) => {
    const normalized = String(text || '')
      .replace(/\s+/g, ' ')
      .trim();
    if (normalized.length <= maxChars) {
      return { preview: normalized, isTruncated: false };
    }
    const slice = normalized.slice(0, maxChars);
    const lastSpace = slice.lastIndexOf(' ');
    const preview = (lastSpace > 80 ? slice.slice(0, lastSpace) : slice).trim();
    return { preview, isTruncated: true };
  };

  return (
    <section id="projects">
      {showHeading && (
        <h2 className="numbered-heading" ref={revealTitle}>
          Some Things I’ve Built
        </h2>
      )}

      {categoryOptions.length > 1 && (
        <StyledFilters aria-label="Featured project filters">
          {categoryOptions.map(category => (
            <li key={category}>
              <StyledFilterButton
                type="button"
                isActive={activeCategory === category}
                onClick={() => setActiveCategory(category)}>
                {category}
              </StyledFilterButton>
            </li>
          ))}
        </StyledFilters>
      )}

      <StyledProjectsGrid>
        {(limit ? filteredFeatured.slice(0, limit) : filteredFeatured).map(({ node }, i) => {
          const { frontmatter, html } = node;
          const { external, title, tech, github, cover, cta, description } = frontmatter;
          const externalUrl = normalizeUrl(external);
          const ctaUrl = normalizeUrl(cta);
          const githubUrl = normalizeUrl(github);
          const primaryHref = externalUrl || ctaUrl || githubUrl || '';
          const image = cover ? getImage(cover) : null;
          const safeHtml = typeof html === 'string' ? html.trim() : '';
          const safeDescription = typeof description === 'string' ? description.trim() : '';
          const fullHtml =
            safeHtml ||
            (safeDescription
              ? `<p>${escapeHtml(safeDescription)}</p>`
              : '<p>More details coming soon.</p>');
          const fullText = plainTextFromHtml(fullHtml);
          const { preview, isTruncated } = truncateText(fullText, 165);

          return (
            <StyledProject key={i} ref={el => (revealProjects.current[i] = el)}>
              {image && (
                <div className="project-image">
                  {primaryHref ? (
                    <a href={primaryHref}>
                      <GatsbyImage image={image} alt={title} className="img" />
                    </a>
                  ) : (
                    <GatsbyImage image={image} alt={title} className="img" />
                  )}
                </div>
              )}

              <div className="project-content">
                <div>
                  <h3 className="project-title">
                    {primaryHref ? <a href={primaryHref}>{title}</a> : <span>{title}</span>}
                  </h3>

                  <p className="project-description">
                    {preview}
                    {isTruncated && (
                      <>
                        …{' '}
                        <button
                          type="button"
                          className="readmore-link"
                          onClick={() => openModal({ title, html: fullHtml })}
                          aria-label={`Read more about ${title}`}>
                          Read more
                        </button>
                      </>
                    )}
                  </p>

                  {tech?.length > 0 && (
                    <ul className="project-tech-list">
                      {tech.map((tech, i) => (
                        <li key={i}>{tech}</li>
                      ))}
                    </ul>
                  )}

                  <div className="project-links">
                    {externalUrl && (
                      <a
                        href={externalUrl}
                        aria-label="View Project"
                        target="_blank"
                        rel="noreferrer">
                        View project <Icon name="External" />
                      </a>
                    )}

                    {ctaUrl && (
                      <a href={ctaUrl} aria-label="Learn More" target="_blank" rel="noreferrer">
                        Learn more <Icon name="External" />
                      </a>
                    )}

                    {githubUrl && (
                      <a
                        href={githubUrl}
                        aria-label="View Code"
                        className="secondary"
                        target="_blank"
                        rel="noreferrer">
                        View code <Icon name="GitHub" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </StyledProject>
          );
        })}
      </StyledProjectsGrid>

      {showViewAllLink && (
        <StyledViewAll>
          <Link to="/built">View all</Link>
        </StyledViewAll>
      )}

      <Modal isOpen={modal.isOpen} title={modal.title} onClose={closeModal}>
        <div dangerouslySetInnerHTML={{ __html: modal.html }} />
      </Modal>
    </section>
  );
};

Featured.propTypes = {
  limit: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  showViewAllLink: PropTypes.bool,
  showHeading: PropTypes.bool,
};

export default Featured;
